import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AnimatedGrid from "../components/ui/AnimatedGrid";
import { getEvents } from "../services/api";
import { fallbackEvents } from "../data/eventFallbacks";

const categoryLabels = {
  entertainment: "Entertainment",
  festivals: "Festivals",
  performances: "Performances",
  exhibitions: "Exhibitions",
};

const statusLabels = {
  all: "All",
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  past: "Past",
};

const getEventStatus = (event) => {
  if (event.status) return event.status;
  if (!event.event_date) return "past";

  const now = Date.now();
  const start = new Date(event.event_date).getTime();
  const end = event.end_date ? new Date(event.end_date).getTime() : null;

  if (end && start <= now && end > now) return "ongoing";
  return start > now ? "upcoming" : "past";
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const isExternalLink = (url = "") => /^https?:\/\//i.test(url);

const sortEvents = (items) => {
  const rank = { ongoing: 0, upcoming: 1, past: 2 };
  return [...items].sort((a, b) => {
    const statusDiff = (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
    if (statusDiff !== 0) return statusDiff;

    const aTime = a.event_date ? new Date(a.event_date).getTime() : 0;
    const bTime = b.event_date ? new Date(b.event_date).getTime() : 0;
    return a.status === "past" ? bTime - aTime : aTime - bTime;
  });
};

const normalizeEvent = (event) => {
  const status = getEventStatus(event);
  return {
    id: event.id,
    image: event.image || event.cover_image_url || event.cover_thumbnail_url || event.cover_image_path || "/images/ad.jpg",
    title: event.title,
    category: event.category,
    categoryLabel: categoryLabels[event.category] || event.category,
    status,
    event_date: event.event_date,
    end_date: event.end_date,
    venue: event.venue,
    city: event.city,
    state: event.state,
    description: event.description || "",
    galleryLink: event.facebook_album_url || "",
    link: event.view_details_url || event.link || (event.slug ? `/events/${event.slug}` : event.facebook_album_url),
  };
};

const EventAction = ({ to, children, className }) => {
  if (!to || to === "#") {
    return <span className={`${className} view-btn-disabled`}>{children}</span>;
  }

  if (isExternalLink(to)) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

const AllEvents = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [events, setEvents] = useState(() => sortEvents(fallbackEvents.map(normalizeEvent)));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents("all", { limit: 100 });
        const apiEvents = (data.events || []).map(normalizeEvent);
        if (apiEvents.length > 0) {
          setEvents(sortEvents(apiEvents));
        }
      } catch (err) {
        setEvents(sortEvents(fallbackEvents.map(normalizeEvent)));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const categories = [
    { id: "all", name: "All Events" },
    { id: "entertainment", name: "Entertainment" },
    { id: "festivals", name: "Festivals" },
    { id: "performances", name: "Performances" },
    { id: "exhibitions", name: "Exhibitions" },
  ];

  const statusFilters = [
    { id: "all", name: "All Statuses" },
    { id: "upcoming", name: "Upcoming" },
    { id: "ongoing", name: "Ongoing" },
    { id: "past", name: "Past" },
  ];

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return events.filter((item) => {
      const categoryMatches = selectedCategory === "all" || item.category === selectedCategory;
      const statusMatches = selectedStatus === "all" || item.status === selectedStatus;
      const searchableText = [
        item.title,
        item.description,
        item.categoryLabel,
        item.venue,
        item.city,
        item.state,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return categoryMatches && statusMatches && (!term || searchableText.includes(term));
    });
  }, [events, searchTerm, selectedCategory, selectedStatus]);

  const clearFilters = () => {
    setSelectedCategory("all");
    setSelectedStatus("all");
    setSearchTerm("");
  };

  return (
    <div className="all-events-page">
      <section className="events-hero">
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
            All Events
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}>
            Explore our cultural events, festivals, artist tours, and community gatherings.
          </motion.p>
        </motion.div>
      </section>

      <section className="filter-section">
        <div className="filter-container">
          <motion.div className="events-search-row" initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.5 }}>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="events-search"
              placeholder="Search title, city, venue, or description"
              aria-label="Search events"
            />
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
              className="events-status-select"
              aria-label="Filter by status"
            >
              {statusFilters.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </motion.div>

          <motion.div className="filter-buttons" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6, duration: 0.5 }}>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`filter-btn ${selectedCategory === category.id ? "active" : ""}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="events-grid-section">
        <div className="events-container">
          <div className="events-result-bar">
            <span>{loading ? "Loading events..." : `${filteredItems.length} event${filteredItems.length === 1 ? "" : "s"}`}</span>
            {(selectedCategory !== "all" || selectedStatus !== "all" || searchTerm) && (
              <button type="button" className="clear-filters-btn" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          {loading ? (
            <div className="events-loading-grid">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div key={item} className="event-skeleton">
                  <div className="event-skeleton-image" />
                  <div className="event-skeleton-body">
                    <span />
                    <strong />
                    <em />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="events-empty-state">
              <h3>No events found</h3>
              <p>Try a different category, status, or search term.</p>
              <button type="button" className="clear-filters-btn clear-filters-btn-large" onClick={clearFilters}>
                Show all events
              </button>
            </div>
          ) : (
            <AnimatedGrid direction="left" columns="repeat(auto-fit, minmax(320px, 1fr))" gap="24px">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="event-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.08, 0.35) }}
                  viewport={{ once: true }}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <div className="card-image-container">
                    <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                    <div className="card-overlay" />
                    <div className={`event-status-pill event-status-${item.status}`}>
                      {statusLabels[item.status] || item.status}
                    </div>
                    <motion.div className="card-hover-content" initial={{ opacity: 0 }} animate={{ opacity: hoveredItem === item.id ? 1 : 0 }} transition={{ duration: 0.3 }}>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <EventAction to={item.link} className="view-btn">
                        View Details
                        <span className="btn-arrow">→</span>
                      </EventAction>
                    </motion.div>
                  </div>
                  <div className="card-content">
                    <div className="card-meta-row">
                      <span className="card-category">{item.categoryLabel || item.category}</span>
                      {item.event_date && <span className="card-date">{formatDate(item.event_date)}</span>}
                    </div>
                    <h4>{item.title}</h4>
                    {(item.city || item.venue) && (
                      <p className="card-location">{[item.venue, item.city, item.state].filter(Boolean).join(", ")}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatedGrid>
          )}
        </div>
      </section>

      <style>{`
        .all-events-page { overflow-x: hidden; }

        .events-hero {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/g.jpg") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
          max-width: 600px;
          margin: 0 auto;
        }

        .filter-section {
          background: #fff;
          padding: 2rem 2rem 0;
          position: relative;
          z-index: 10;
          margin-top: -40px;
        }

        .filter-container {
          max-width: 1200px;
          margin: 0 auto;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
        }

        .events-search-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 190px;
          gap: 1rem;
          padding: 1.2rem 1.3rem 0;
        }

        .events-search,
        .events-status-select {
          width: 100%;
          min-height: 46px;
          border: 1px solid #e4e4e4;
          border-radius: 8px;
          background: #f8f8f8;
          color: #1c1c1c;
          padding: 0 1rem;
          font-size: 0.92rem;
          outline: none;
          transition: border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1), background 0.2s ease;
        }

        .events-search:focus,
        .events-status-select:focus {
          border-color: #C72631;
          background: #fff;
        }

        .filter-buttons {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          padding: 1.2rem 2rem 1.5rem;
        }

        .filter-btn {
          min-height: 44px;
          background: #f7f7f7;
          border: 2px solid transparent;
          color: #666;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .filter-btn:hover {
          background: #fff;
          border-color: #C72631;
          color: #C72631;
        }

        .filter-btn.active {
          background: #C72631;
          border-color: #C72631;
          color: white;
        }

        .events-grid-section {
          padding: 4rem 2rem 6rem;
          background: #fff;
        }

        .events-container {
          max-width: 1300px;
          margin: 0 auto;
        }

        .events-result-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1.25rem;
          color: #666;
          font-size: 0.92rem;
          font-weight: 600;
        }

        .clear-filters-btn {
          border: 0;
          background: transparent;
          color: #C72631;
          font-weight: 700;
          cursor: pointer;
          padding: 0.25rem 0;
        }

        .clear-filters-btn:hover {
          color: #981d25;
        }

        .events-loading-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
        }

        .event-skeleton,
        .event-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
          border: 1px solid #f0f0f0;
        }

        .event-skeleton-image,
        .event-skeleton-body span,
        .event-skeleton-body strong,
        .event-skeleton-body em {
          display: block;
          background: linear-gradient(90deg, #eeeeee, #f7f7f7, #eeeeee);
          background-size: 240% 100%;
          animation: eventShimmer 1.4s ease-in-out infinite;
        }

        .event-skeleton-image { height: 280px; }
        .event-skeleton-body { padding: 1.5rem; }
        .event-skeleton-body span { width: 34%; height: 20px; border-radius: 8px; margin-bottom: 1rem; }
        .event-skeleton-body strong { width: 76%; height: 24px; border-radius: 8px; margin-bottom: 0.75rem; }
        .event-skeleton-body em { width: 54%; height: 16px; border-radius: 8px; }

        @keyframes eventShimmer {
          0% { background-position: 120% 0; }
          100% { background-position: -120% 0; }
        }

        .events-empty-state {
          border: 1px solid #eeeeee;
          border-radius: 16px;
          background: #fafafa;
          text-align: center;
          padding: 4rem 2rem;
        }

        .events-empty-state h3 {
          margin: 0 0 0.6rem;
          color: #0a0a0a;
          font-size: 1.5rem;
        }

        .events-empty-state p {
          margin: 0 0 1.3rem;
          color: #666;
        }

        .clear-filters-btn-large {
          background: #C72631;
          color: #fff;
          border-radius: 8px;
          padding: 0.75rem 1.25rem;
        }

        .clear-filters-btn-large:hover {
          color: #fff;
          background: #981d25;
        }

        .event-card {
          transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .event-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
          border-color: transparent;
        }

        .card-image-container {
          position: relative;
          height: 280px;
          overflow: hidden;
        }

        .card-image-container img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .event-card:hover .card-image-container img {
          transform: scale(1.08);
        }

        .card-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.3);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .event-card:hover .card-overlay {
          background: rgba(10, 10, 10, 0.85);
        }

        .event-status-pill {
          position: absolute;
          top: 1rem;
          left: 1rem;
          z-index: 2;
          border-radius: 999px;
          padding: 0.4rem 0.75rem;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          background: rgba(10, 10, 10, 0.78);
        }

        .event-status-upcoming { background: #C72631; }
        .event-status-ongoing { background: #111; }
        .event-status-past { background: rgba(255,255,255,0.9); color: #111; }

        .card-hover-content {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          text-align: center;
        }

        .card-hover-content h3 {
          color: white;
          font-size: 1.3rem;
          font-weight: 800;
          margin: 0 0 1rem;
        }

        .card-hover-content p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 0 0 1.5rem;
          max-height: 5.7rem;
          overflow: hidden;
        }

        .view-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          color: #0a0a0a;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.85rem;
          text-decoration: none;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .view-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .view-btn-disabled {
          opacity: 0.7;
          cursor: default;
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .view-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        .card-content {
          padding: 1.5rem;
        }

        .card-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .card-category {
          display: inline-block;
          background: rgba(199, 38, 49, 0.08);
          color: #C72631;
          padding: 0.3rem 0.75rem;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .card-date {
          color: var(--text-muted-light, #6e6e6e);
          font-size: 0.78rem;
          font-weight: 700;
          white-space: nowrap;
        }

        .card-content h4 {
          color: #0a0a0a;
          font-size: 1.1rem;
          font-weight: 700;
          margin: 0;
          line-height: 1.4;
        }

        .card-location {
          margin: 0.45rem 0 0;
          color: var(--text-muted-light, #6e6e6e);
          font-size: 0.86rem;
          line-height: 1.45;
        }

        @media (max-width: 768px) {
          .events-hero { min-height: 50vh; }
          .filter-section { padding-left: 1rem; padding-right: 1rem; }
          .events-search-row { grid-template-columns: 1fr; padding: 1rem 1rem 0; }
          .filter-buttons { gap: 0.75rem; padding: 1rem; }
          .filter-btn { padding: 0.6rem 1rem; font-size: 0.85rem; min-height: 44px; }
          .card-image-container, .event-skeleton-image { height: 220px; }
          .events-grid-section { padding: 3rem 1.5rem 4rem; }
          .events-result-bar { align-items: flex-start; flex-direction: column; }
        }

        @media (max-width: 480px) {
          .hero-content h1 { font-size: 2rem; }
          .filter-btn { padding: 0.5rem 0.85rem; font-size: 0.8rem; }
          .card-meta-row { align-items: flex-start; flex-direction: column; gap: 0.5rem; }
        }
      `}</style>
    </div>
  );
};

export default AllEvents;
